import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Header.css';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();
  const dropdownTimerRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change or Escape key
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsMobileMenuOpen(false);
        setActiveDropdown(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  const openDropdown = (name) => {
    clearTimeout(dropdownTimerRef.current);
    setActiveDropdown(name);
  };

  const closeDropdown = () => {
    dropdownTimerRef.current = setTimeout(() => setActiveDropdown(null), 150);
  };

  const isActive = (item) => {
    if (item.path === '/') {
      return location.pathname === '/';
    }
    if (item.dropdown) {
      return location.pathname.startsWith(item.basePath || item.path);
    }
    return location.pathname === item.path || location.pathname.startsWith(item.path + '/');
  };

  const navItems = [
    { label: 'Home', path: '/' },
    {
      label: 'About',
      path: '/about/who-are-we',
      basePath: '/about',
      dropdown: [
        { label: 'Who Are We', path: '/about/who-are-we', desc: 'Our story, mission & values' },
        { label: 'Why It Matters', path: '/about/why-it-matters', desc: 'The urgency of African youth empowerment' },
        { label: 'Our Team', path: '/about/our-team', desc: 'Meet the passionate leaders behind YOCILAB' },
      ],
    },
    {
      label: 'Academy',
      path: '/programs/yocilab-academy',
      basePath: '/programs/yocilab-academy',
      dropdown: [
        { label: 'YOCILAB Academy', path: '/programs/yocilab-academy', desc: 'Skills, AI & future of work' },
        { label: 'ABETA Platform', path: '/programs/abeta', desc: 'AI-powered Builders Platform' },
        { label: 'Creative Space', path: '/programs/yocilab-hub', desc: 'Co-creation & incubation hubs' },
      ],
    },
    {
      label: 'Youth Summit',
      path: '/youth-summit',
      basePath: '/youth-summit',
      dropdown: [
        { label: 'Overview', path: '/youth-summit', desc: '30th & 31st Oct 2026 · Bamenda' },
        { label: 'Register for Summit', path: '/youth-summit/register', desc: 'Secure your delegate pass or team spot' },
        { label: 'Campus Tour', path: '/youth-summit/campus-tour', desc: 'Pre-Summit university outreach' },
        { label: 'Summit Experience', path: '/youth-summit/summit-experience', desc: 'Two days of keynotes & career studio' },
        { label: 'Innovation Challenge', path: '/youth-summit/innovation-challenge', desc: 'Overnight hack night sprint' },
        { label: 'Fellowship', path: '/youth-summit/fellowship', desc: '6-month post-Summit leadership journey' },
      ],
    },
    { label: 'Creative Space', path: '/programs/yocilab-hub' },
    { label: 'Ventures', path: '/ventures' },
    { label: 'Donate', path: '/donate' },
    { label: 'Contact', path: '/contact' },
  ];

  return (
    <>
    <header className={`header ${scrolled ? 'header--scrolled' : ''} ${isMobileMenuOpen ? 'header--menu-open' : ''}`}>
      <div className="header__inner">
        {/* Logo */}
        <Link to="/" className="header__logo">
          <img
            src={require('../assets/img/YOCILAB-01.png')}
            alt="YOCILAB"
            className="header__logo-img"
          />
        </Link>

        {/* Desktop Navigation */}
        <nav className="header__nav" aria-label="Main navigation">
          <ul className="header__nav-list">
            {navItems.map((item) => {
              const active = isActive(item);
              return (
                <li
                  key={item.label}
                  className={`header__nav-item ${item.dropdown ? 'header__nav-item--dropdown' : ''}`}
                  onMouseEnter={() => item.dropdown && openDropdown(item.label)}
                  onMouseLeave={() => item.dropdown && closeDropdown()}
                >
                  <Link
                    to={item.path}
                    className={`header__nav-link ${active ? 'header__nav-link--active' : ''}`}
                  >
                    <span>{item.label}</span>
                    {item.dropdown && (
                      <span className={`header__nav-arrow ${activeDropdown === item.label ? 'header__nav-arrow--open' : ''}`}>
                        <svg width="9" height="6" viewBox="0 0 10 6" fill="none">
                          <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                    )}
                  </Link>

                  {item.dropdown && (
                    <div
                      className={`header__dropdown ${activeDropdown === item.label ? 'header__dropdown--open' : ''}`}
                      onMouseEnter={() => openDropdown(item.label)}
                      onMouseLeave={closeDropdown}
                    >
                      <div className="header__dropdown-inner">
                        {item.dropdown.map((sub) => {
                          const subActive = location.pathname === sub.path;
                          return (
                            <Link
                              key={sub.path}
                              to={sub.path}
                              className={`header__dropdown-item ${subActive ? 'header__dropdown-item--active' : ''}`}
                            >
                              <span className="header__dropdown-label">{sub.label}</span>
                              <span className="header__dropdown-desc">{sub.desc}</span>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Desktop CTA */}
        <div className="header__cta">
          <Link to="/youth-summit/register" className="header__cta-btn">
            Register for Summit
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2 7H12M12 7L8 3M12 7L8 11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Link>
        </div>

        {/* Mobile Menu Toggle */}
        <button
          className={`header__mobile-toggle ${isMobileMenuOpen ? 'header__mobile-toggle--open' : ''}`}
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle navigation menu"
          aria-expanded={isMobileMenuOpen}
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>

    </header>

      {/* Mobile Menu Backdrop */}
      {isMobileMenuOpen && (
        <div
          className="header__mobile-backdrop"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Mobile Menu */}
      <div className={`header__mobile-menu ${isMobileMenuOpen ? 'header__mobile-menu--open' : ''}`} aria-hidden={!isMobileMenuOpen}>
        <div className="header__mobile-inner">
          <ul className="header__mobile-list">
            {navItems.map((item) => {
              const active = isActive(item);
              return (
                <li key={item.label} className="header__mobile-item">
                  {item.dropdown ? (
                    <>
                      <button
                        className={`header__mobile-toggle-btn ${active ? 'header__mobile-toggle-btn--active' : ''} ${activeDropdown === item.label ? 'header__mobile-toggle-btn--open' : ''}`}
                        onClick={() => setActiveDropdown(activeDropdown === item.label ? null : item.label)}
                        aria-expanded={activeDropdown === item.label}
                      >
                        <span>{item.label}</span>
                        <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                          <path d="M1 1L6 7L11 1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                      <div className={`header__mobile-dropdown ${activeDropdown === item.label ? 'header__mobile-dropdown--open' : ''}`}>
                        {item.dropdown.map((sub) => {
                          const subActive = location.pathname === sub.path;
                          return (
                            <Link
                              key={sub.path}
                              to={sub.path}
                              className={`header__mobile-sub-link ${subActive ? 'header__mobile-sub-link--active' : ''}`}
                              onClick={() => setIsMobileMenuOpen(false)}
                            >
                              <span className="header__mobile-sub-label">{sub.label}</span>
                              <span className="header__mobile-sub-desc">{sub.desc}</span>
                            </Link>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <Link
                      to={item.path}
                      className={`header__mobile-link ${active ? 'header__mobile-link--active' : ''}`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {item.label}
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>

          <div className="header__mobile-footer">
            <Link to="/youth-summit/register" className="header__mobile-cta" onClick={() => setIsMobileMenuOpen(false)}>
              Register for Summit 2026
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M2 7H12M12 7L8 3M12 7L8 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
